import { useBodyEditor } from "../context/GrapeJsBodyContext";
import { useFooterEditor } from "../context/GrapeJsFooterContext";
import { useHeaderEditor } from "../context/GrapeJsHeaderContext";
import { useParams } from "react-router";
import APIS from "../constant/Endpoint";
import usePatchHook from "./usePatchHook";
import usePostHook from "./usePostHook";
import { convertGrapeJSToDjangoTemlate } from "../utils/grapejs";
import { Editor } from "grapesjs";
import { postApiFormData } from "../helpers/AxiosInstance";
import useGrapesjsHook from "./useGrapesjsHook";
import { html } from "../utils/tw";

const useEditor = () => {
  let { id } = useParams();
  const { postAsync: footerUpdate } = useGrapesjsHook({
    queryKey: [`footer`],
    navigateURL: "",
  });
  const { postAsync: headerUpdate } = useGrapesjsHook({
    queryKey: [`header`],
    navigateURL: "",
  });
  const { patchAsync: bodyUpdate } = useGrapesjsHook({
    queryKey: [`page-${id}`],
    navigateURL: "",
  });
  const { editor: bodyEditor } = useBodyEditor();
  const { editor: footerEditor } = useFooterEditor();
  const { editor: headerEditor } = useHeaderEditor();

  const handleDeviceChange = (deviceId: string | undefined) => {
    if (!deviceId) return;
    bodyEditor?.Devices.select(deviceId);
    footerEditor?.Devices.select(deviceId);
    headerEditor?.Devices.select(deviceId);
  };

  const handleMediaUpload = async (file, editor: Editor) => {
    const url = APIS.FILE;
    const formData = new FormData();
    formData.append("file", file[0]);
    formData.append("object_id", id);
    formData.append("folder", "page-builder");
    try {
      const res = await postApiFormData({ url, formData });
      if (res.status === 201) {
        const fileUrl = res?.data?.file;
        if (editor?.AssetManager) {
          editor.AssetManager.add({ src: fileUrl });
          const selected = editor.getSelected();
          if (selected && selected.is("image")) {
            selected.set("src", fileUrl);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePanelChange = async (panelId: string) => {
    // const panelManager = editor.Panels;

    switch (panelId) {
      case "save":
        const headerURL = APIS.HEADER;
        const footerURL = APIS.FOOTER;
        const pageURL = `${APIS.PAGE}${id}/`;
        await Promise.all([
          bodyUpdate({
            url: pageURL,
            formData: {
              css_content: bodyEditor.getCss(),
              html_content: await convertGrapeJSToDjangoTemlate(
                bodyEditor.getHtml()
              ),
              json_content: bodyEditor.Blocks.getAllMap(),
              project_data: bodyEditor.getProjectData(),
            },
          }),
          bodyEditor.getModel().set("changesCount", 0),
          headerUpdate({
            url: headerURL,
            formData: {
              css_content: headerEditor.getCss(),
              html_content: await convertGrapeJSToDjangoTemlate(
                headerEditor.getHtml()
              ),
              json_content: headerEditor.Blocks.getAllMap(),
              project_data: headerEditor.getProjectData(),
            },
          }),
          headerEditor.getModel().set("changesCount", 0),
          footerUpdate({
            url: footerURL,
            formData: {
              css_content: footerEditor.getCss(),
              html_content: await convertGrapeJSToDjangoTemlate(
                footerEditor.getHtml()
              ),
              json_content: footerEditor.Blocks.getAllMap(),
              project_data: footerEditor.getProjectData(),
            },
          }),
          footerEditor.getModel().set("changesCount", 0),
        ]);
        break;
      case "undo":
        bodyEditor?.UndoManager.undo();
        break;
      case "redo":
        bodyEditor?.UndoManager.redo();

        break;
      case "trash":
        bodyEditor?.DomComponents.clear();
        bodyEditor?.CssComposer.clear();
        bodyEditor.getWrapper().set("script", "");
        break;
      case "visibility":
        const body = bodyEditor.getHtml();
        const header = headerEditor.getHtml();
        const footer = footerEditor.getHtml();
        const bodyCss = bodyEditor.getCss();
        const headerCss = headerEditor.getCss();
        const footerCss = footerEditor.getCss();
        const previewWindow = window.open("", "_blank");
        previewWindow?.document.write(`
          <html>
            <head>
              <style>
                ${headerCss}
              </style>
              <style>
                ${footerCss}
              </style>
              <style>
                ${bodyCss}
              </style>
              <style>
                :root {
                  --primary: #850000;
                  --secondary: #b80000;
                  --accent: 136, 58, 234;
                  --accent-light: 224, 204, 250;
                  --accent-dark: 49, 10, 101;
                  --accent-gradient: linear-gradient(
                    45deg,
                    rgb(var(--accent)),
                    rgb(var(--accent-light)) 30%,
                    white 60%
                  );
                }

                .ripple {
                  position: absolute;
                  border-radius: 50%;
                  transform: scale(0);
                  animation: ripple 800ms linear;
                  background-color: rgba(255, 255, 255, 0.5);
                  pointer-events: none;
                }

                @keyframes ripple {
                  to {
                    transform: scale(4);
                    opacity: 0;
                  }
                }
              </style>
              <style>
                ::-webkit-scrollbar {
                  display: none;
                }
                body {
                  -ms-overflow-style: none; /* IE and Edge */
                  scrollbar-width: none; /* Firefox */
                }
              </style>
              <script src="https://careinfo-solutions.s3.ap-south-1.amazonaws.com/tailwind/tailwind.min.js"></script>
              <link
                rel="stylesheet"
                href="https://cdn.jsdelivr.net/npm/toastify-js/src/toastify.min.css"
              />
              <link
                rel="stylesheet"
                href="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css"
              />
            </head>
            <header>${header}</header>
            <body>
              ${body}
            </body>
            <footer>${footer}</footer>
          </html>
        `);
        previewWindow?.document.close();
        break;

      case "fullscreen":
        bodyEditor?.Commands.run("fullscreen");
        break;
      case "setting":
        bodyEditor?.Commands.run("setting-popup");
        break;
      case "seo":
        bodyEditor?.Commands.run("seo-popup");
        break;
      case "export":
        bodyEditor?.Commands.run("export-template");
        break;
      case "outline":
        bodyEditor?.Commands.isActive("core:component-outline")
          ? bodyEditor?.Commands.stop("core:component-outline")
          : bodyEditor?.Commands.run("core:component-outline");
        break;
      default:
        break;
    }
  };

  return { handleDeviceChange, handlePanelChange, handleMediaUpload };
};

export default useEditor;
